import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EyeOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Typography, Tooltip } from 'antd';
import { intialPaginationState, filter, unexpectedError, showingPageTotalRange, onlyYear } from '../../../common_functions';
import { PAPER_SHEEN, OBA, PAPER_WEIGHT, PAPER_TEXTURE } from '../../../config';
import moment from 'moment';

const StatsList = props => {
  const { id } = useParams();
  const [statsData, setStatsData] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [sorter, setSorter] = useState(null);
  const [loading, setLoading] = useState(false);
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'id',
      key: 'id',
      render: (value, item, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: 'Measurement Date',
      dataIndex: 'date_created',
      key: 'date_created',
      render: date => moment(date).format('MM/DD/YYYY, h:mm:ss A'),
    },
    { title: 'Paper Family Name', dataIndex: 'paper_family_name', key: 'paper_family_name', ...filter('paper_family_name') },
    { title: 'Paper Group Name', dataIndex: 'paper_group_name', key: 'paper_group_name', ...filter('paper_group_name') },
    { title: 'Paper Group Code', dataIndex: 'paper_group_code', key: 'paper_group_code', ...filter('code') },
    { title: 'Population', dataIndex: 'population', key: 'population' },
    {
      title: 'Print Date Earliest Early',
      dataIndex: 'print_date_earliest_early',
      key: 'print_date_earliest_early',
      render: date => onlyYear(date),
    },
    {
      title: 'Print Date Earliest Average',
      dataIndex: 'print_date_earliest_avg',
      key: 'print_date_earliest_avg',
      render: date => onlyYear(date),
    },
    {
      title: 'Print Date Latest Late',
      dataIndex: 'print_date_latest_late',
      key: 'print_date_latest_late',
      render: date => onlyYear(date),
    },
    {
      title: 'Print Date Latest Average',
      dataIndex: 'print_date_latest_avg',
      key: 'print_date_latest_avg',
      render: date => onlyYear(date),
    },
    {
      title: 'Group Stat Date Smallest Range',
      dataIndex: 'group_stat_date_smallest_range',
      key: 'group_stat_date_smallest_range',
      ...filter('group_stat_date_smallest_range'),
    },
    { title: 'Smallest Date Range', dataIndex: 'smallest_date_range', key: 'smallest_date_range' },
    {
      title: 'Date Confidence Earliest',
      dataIndex: 'highest_date_confidence_earliest',
      key: 'highest_date_confidence_earliest',
      ...filter('highest_date_confidence_earliest'),
    },
    {
      title: 'Date Confidence Latest',
      dataIndex: 'highest_date_confidence_latest',
      key: 'highest_date_confidence_latest',
      ...filter('highest_date_confidence_latest'),
    },

    {
      title: 'Date Confidence Avg',
      dataIndex: 'date_confidence_average',
      key: 'date_confidence_average',
      ...filter('date_confidence_average'),
    },
    {
      title: 'Stamp Date Earliest Early',
      dataIndex: 'stamp_date_earliest_early',
      key: 'stamp_date_earliest_early',
      render: date => onlyYear(date),
    },
    {
      title: 'Stamp Date Latest Late',
      dataIndex: 'stamp_date_latest_late',
      key: 'stamp_date_latest_late',
      render: date => onlyYear(date),
    },
    {
      title: 'Authenticity Confidence Low',
      dataIndex: 'authenticity_confidence_low',
      key: 'authenticity_confidence_low',
      ...filter('authenticity_confidence_low'),
    },
    {
      title: 'Authenticity Confidence High',
      dataIndex: 'authenticity_confidence_high',
      key: 'authenticity_confidence_high',
      ...filter('authenticity_confidence_high'),
    },
    {
      title: 'Authenticity Confidence Avg',
      dataIndex: 'authenticity_confidence_avg',
      key: 'authenticity_confidence_avg',
      ...filter('authenticity_confidence_avg'),
    },
    {
      title: 'Ba / Sr Ratio Low',
      dataIndex: 'ba_sr_ratio_low',
      key: 'ba_sr_ratio_low',
      ...filter('ba_sr_ratio_low'),
    },
    {
      title: 'Ba / Sr Ratio High',
      dataIndex: 'ba_sr_ratio_high',
      key: 'ba_sr_ratio_high',
      ...filter('ba_sr_ratio_high'),
    },
    {
      title: 'Ba / Sr Ratio Avg',
      dataIndex: 'ba_sr_ratio_avg',
      key: 'ba_sr_ratio_avg',
      ...filter('ba_sr_ratio_avg'),
    },
    {
      title: 'Ba / Sr Ratio St Dev',
      dataIndex: 'ba_sr_ratio_st_dev',
      key: 'ba_sr_ratio_st_dev',
      ...filter('ba_sr_ratio_st_dev'),
    },
    {
      title: 'Ba Absolute Value Low',
      dataIndex: 'ba_absolute_value_low',
      key: 'ba_absolute_value_low',
      ...filter('ba_absolute_value_low'),
    },
    {
      title: 'Ba Absolute Value High',
      dataIndex: 'ba_absolute_value_high',
      key: 'ba_absolute_value_high',
      ...filter('ba_absolute_value_high'),
    },
    {
      title: 'Ba Absolute Value Avg',
      dataIndex: 'ba_absolute_value_avg',
      key: 'ba_absolute_value_avg',
      ...filter('ba_absolute_value_avg'),
    },
    {
      title: 'Ba Absolute Value St Dev',
      dataIndex: 'ba_absolute_value_standard_dev',
      key: 'ba_absolute_value_standard_dev',
      ...filter('ba_absolute_value_standard_dev'),
    },
    {
      title: 'Sr Absolute Value Low',
      dataIndex: 'sr_absolute_value_low',
      key: 'sr_absolute_value_low',
      ...filter('sr_absolute_value_low'),
    },
    {
      title: 'Sr Absolute Value High',
      dataIndex: 'sr_absolute_value_high',
      key: 'sr_absolute_value_high',
      ...filter('sr_absolute_value_high'),
    },
    {
      title: 'Sr Absolute Value Avg',
      dataIndex: 'sr_absolute_value_avg',
      key: 'sr_absolute_value_avg',
      ...filter('sr_absolute_value_avg'),
    },
    {
      title: 'Sr Absolute Value St Dev',
      dataIndex: 'sr_absolute_value_standard_dev',
      key: 'sr_absolute_value_standard_dev',
      ...filter('sr_absolute_value_standard_dev'),
    },
    {
      title: 'Optical Brightener',
      dataIndex: 'optical_brightener',
      key: 'optical_brightener',
      render: oba => {
        const matchedItem = OBA.find(item => item.key == oba);
        return matchedItem ? <>{matchedItem.value}</> : null;
      },
    },
    {
      title: 'Paper Texture',
      dataIndex: 'paper_texture',
      key: 'paper_texture',
    },
    {
      title: 'Paper Weight',
      dataIndex: 'paper_weight',
      key: 'paper_weight',
      render: text => {
        const matchedItem = PAPER_WEIGHT.find(item => item.key == text);
        return matchedItem ? <>{matchedItem.value}</> : null;
      },
    },
    {
      title: 'Paper Thickness Low',
      dataIndex: 'paper_thickness_low',
      key: 'paper_thickness_low',
      ...filter('paper_thickness_low'),
    },
    {
      title: 'Paper Thickness High',
      dataIndex: 'paper_thickness_high',
      key: 'paper_thickness_high',
      ...filter('paper_thickness_high'),
    },
    {
      title: 'Paper Thickness Avg',
      dataIndex: 'paper_thickness_avg',
      key: 'paper_thickness_avg',
      ...filter('paper_thickness_avg'),
    },
    {
      title: 'Paper Thickness St Dev',
      dataIndex: 'paper_thickness_st_dev',
      key: 'paper_thickness_st_dev',
      ...filter('paper_thickness_st_dev'),
    },
    {
      title: 'Paper Color',
      dataIndex: 'paper_color',
      key: 'paper_color',
      ...filter('paper_color'),
    },
    ...(id
      ? []
      : [
          {
            title: 'Actions',
            key: 'action',
            render: (text, record) => (
              <span>
                <div className="actionbtngroup">
                  <Link to={`/settings/paper-group-stats/view/${record.paper_group}`}>
                    <Tooltip placement="top" title="View paper group history">
                      <button className="edit-btn">
                        <EyeOutlined />
                      </button>
                    </Tooltip>
                  </Link>
                </div>
              </span>
            ),
          },
        ]),
  ];
  //Get collection data
  useEffect(() => {
    (async () => {
    await getGroupStatsData(pagination);
  })();
  }, []);
  const getGroupStatsData = async (pagination, filters = {}, sorter = '') => {
    try {
      id ? (filters['paper_group'] = { _eq: id }) : (filters['latest_record'] = { _eq: 1 });
      setLoading(true);
      const response = await props.getItems('paper_group_stats', {
        fields: ['*'],
        sort: sorter?.field ? (sorter?.order === 'ascend' ? '' : '-') + sorter?.field : '-id',
        filters: filters,
        offset: pagination.pageSize * (pagination.current - 1),
        limit: pagination.pageSize,
        meta: 'filter_count',
      });

      pagination.total = response.meta.filter_count;
      setPagination(pagination);
      setStatsData(response.data);
    } catch (error) {
      console.log('error :', error);

      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
    setLoading(true);
    let filtersitems = {};
    for (let key in filters) {
      if (filters.hasOwnProperty(key)) {
        let val = filters[key];
        if (val) {
          filtersitems[key] = { _icontains: val[0] };
        }
      }
    }
    await getGroupStatsData(pagination, filtersitems, sorter);
  };
  return (
    <Row>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Settings", href: `/settings/paper-group-stats` }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                View Paper Group Stats
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist-list">
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              bordered
              rowKey={record => record.id}
              dataSource={statsData}
              pagination={{ ...pagination, ...showingPageTotalRange }}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default StatsList;
